"use client"

import { useState } from "react"

import { useTranslations } from "next-intl"

import { PRICE_TIERS } from "@/components/marketplace/bnpl/price-tiers"
import { moneyShort } from "@/components/marketplace/format"
import { Button, Pill, RangeSlider, Stack, Text } from "@/components/ui"

interface PriceFacetProps {
  min?: number
  max?: number
  onChange: (min?: number, max?: number) => void
}

type Range = [number, number]

const FLOOR = 0
const CEILING = 5000
const STEP = 100

const toRange = (min?: number, max?: number): Range => [min === undefined ? FLOOR : Math.min(min, CEILING), max === undefined ? CEILING : Math.min(max, CEILING)]

const bandText = (low: number, high: number, open: boolean) => (open ? `${moneyShort(low)}+` : `${moneyShort(low)}–${moneyShort(high)}`)

export const PriceFacet = ({ min, max, onChange }: PriceFacetProps) => {
  const t = useTranslations("search.priceBand")
  const [dragging, setDragging] = useState<Range | null>(null)
  const [low, high] = dragging ?? toRange(min, max)
  const open = high >= CEILING
  const active = min !== undefined || max !== undefined
  const activeTier = PRICE_TIERS.findIndex((tier) => tier.min === min && tier.max === max)

  const commit = ([nextLow, nextHigh]: Range) => {
    setDragging(null)
    onChange(nextLow > FLOOR ? nextLow : undefined, nextHigh < CEILING ? nextHigh : undefined)
  }

  return (
    <Stack gap="300">
      <div className="flex items-start justify-between gap-300">
        <Text as="span" variant="body" family="text" tone="strong" numeric className="min-w-0 font-medium">
          {bandText(low, high, open)}
        </Text>
        {active && (
          <Button variant="quiet" onClick={() => onChange(undefined, undefined)} className="text-body shrink-0 font-normal underline">
            {t("clear")}
          </Button>
        )}
      </div>
      <RangeSlider
        min={FLOOR}
        max={CEILING}
        step={STEP}
        value={[low, high]}
        onChange={setDragging}
        onCommit={commit}
        lowLabel={t("lowHandle")}
        highLabel={t("highHandle")}
        formatValue={moneyShort}
      />
      <div className="relative">
        <div className="flex scrollbar-none gap-125 overflow-x-auto" role="group" aria-label={t("question")}>
          {PRICE_TIERS.map((tier, index) => {
            const selected = index === activeTier
            return (
              <Pill
                key={tier.label}
                tone="outline"
                size="sm"
                selected={selected}
                className="text-body h-9 font-normal tabular-nums"
                onClick={() => (selected ? onChange(undefined, undefined) : onChange(tier.min, tier.max))}
              >
                {tier.label}
              </Pill>
            )
          })}
        </div>
        <div aria-hidden className="from-surface pointer-events-none absolute inset-y-0 right-0 w-6.5 bg-gradient-to-l to-transparent" />
      </div>
    </Stack>
  )
}
